import { Breadcrumb, Button, Input, Modal, Select, Spin, Switch } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMapPortalFileUpload } from '../../../hooks/useMapPortalFileUpload';
import FileUpload from '../UploadData/FileUpload';
import HintLabel from '../components/HintLabel';
import UploadProgressModal from '../components/UploadProgressModal';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import { FileTypes } from '../../../enums/fileTypes';
import FileManagerService from '../../../services/FileManager/fileManager.service';
import { QueryKeys } from '../../../enums/queryKeys';
import { fileTypeMap, ROOT_DISPLAY_NAME } from '../../FileManager/constants';
import { useAuth } from '../../../context/AuthContext';
import { LayerAction } from '../../../enums/mapPortalActions';
import { layerActionAccess } from '../constants';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import './LayerProperties.scss';
import { UploadFile } from 'antd/es/upload/interface';
import { DISPLAY_IN_LEGEND_HINT, DISPLAY_ON_MAP_HINT, DOWNLOADABLE_HINT } from './constants';

const { Option } = Select;
interface LayerSettingsTabProps {
  setIsReUpload?: React.Dispatch<React.SetStateAction<boolean>>;
}

const mapScales = [
  { label: '1:200,000,000', value: 200000000 },
  { label: '1:100,000,000', value: 100000000 },
  { label: '1:50,000,000', value: 50000000 },
  { label: '1:25,000,000', value: 25000000 },
  { label: '1:10,000,000', value: 10000000 },
  { label: '1:1,000,000', value: 1000000 },
  { label: '1:500,000', value: 500000 },
  { label: '1:100,000', value: 100000 },
  { label: '1:50,000', value: 50000 },
  { label: '1:10,000', value: 10000 },
  { label: '1:1,000', value: 1000 },
  { label: '1:100', value: 100 },
];

const LayerSettingsTab: React.FC<LayerSettingsTabProps> = ({ setIsReUpload }) => {
  const { layerDescription, scaleRange, displayOnMap, displayInLegend, downloadable, folderId, layerTitle, setState, isExistingLayer, sourceFormat } =
    useLayerProperitesContext();

  const { canAccess } = useAuth();
  const canReupload = canAccess(layerActionAccess(LayerAction.RE_UPLOAD_DATA));

  const [allowedExtensions, setAllowedExtensions] = useState<string[]>(fileTypeMap[FileTypes.SHAPEFILE]);

  // Shares the folder picker's query, so the breadcrumb paints from cache
  // instead of this tab refetching the whole mapping on every open.
  const { getFileMapping } = FileManagerService();
  const { data: folderMapping, isPending: isMappingPending } = useQuery(getFileMapping());
  const fileMap = folderMapping?.fileMap ?? {};
  const rootFolderId = folderMapping?.rootFolderId ?? '';

  const queryClient = useQueryClient();
  const refreshFolderMapping = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_FOLDERS_FOR_SELECTION] }),
    [queryClient],
  );

  const { handleFileChange, selectedFiles, uploadProgress, isUploading, presignedFileUpload, cancelUpload, setSelectedFiles } =
    useMapPortalFileUpload(folderId, refreshFolderMapping);

  const { setS3UniqueFolderName, setUploadedFiles } = useLegendForm();
  const { reuploadRequested, setReuploadRequested } = useLegendUI();

  const [isReuploadVisible, setIsReuploadVisible] = useState(false);
  const [validationError, setValidationError] = useState('');

  const sourcePath = useMemo(() => {
    if (!folderId) return [];

    const path: Array<{ id: string; name: string }> = [];
    const visited = new Set<string>();
    let currentId: string | undefined = folderId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const folder = fileMap[currentId] as any;
      path.unshift({
        id: currentId,
        name: currentId === rootFolderId ? ROOT_DISPLAY_NAME : folder?.name || currentId,
      });
      currentId = folder?.parentId;
    }

    return path;
  }, [fileMap, folderId, rootFolderId]);

  const handleReUploadData = () => {
    // Each time the user opens the modal, start from a clean slate so the
    // previous session's file list (which AntD Upload keeps around because
    // the LayerProperties Modal is not destroyed on close) doesn't bleed in.
    setSelectedFiles([]);
    setIsReuploadVisible(true);
    setIsReUpload?.(true);
  };

  useEffect(() => {
    setAllowedExtensions(fileTypeMap[sourceFormat]);
  }, [sourceFormat]);

  // US-101368 Scenario 1: the 3-dot menu's "Re-Upload Data" sets
  // reuploadRequested. As soon as the Settings tab is mounted (i.e. the user
  // has Layer Properties open on the right layer), we auto-launch the
  // re-upload modal and clear the flag so it doesn't fire again on tab
  // switches.
  useEffect(() => {
    if (reuploadRequested && isExistingLayer) {
      setReuploadRequested(false);
      // Fires from a context flag, not a click.
      if (canReupload) handleReUploadData();
    }
  }, [reuploadRequested, isExistingLayer, canReupload]);

  // Sync selected files to context for LegendDrawer to use
  useEffect(() => {
    if (isReuploadVisible) {
      setUploadedFiles(selectedFiles);
    }
  }, [selectedFiles, isReuploadVisible, setUploadedFiles]);

  // Clear inline validation error whenever the file selection changes
  useEffect(() => {
    setValidationError('');
  }, [selectedFiles]);

  // US-101368 Scenarios 3 & 4: validate the file set against the existing
  // layer's source format before kicking off the upload.
  //   - Single-file formats (KML, GeoJSON, etc.): at least one file whose
  //     extension matches the allowed list for that sourceFormat.
  //   - Shapefile: must include .shp, .shx, .dbf, .prj with the same base
  //     filename (case-insensitive).
  const validateReuploadFiles = (): { ok: true } | { ok: false; message: string } => {
    if (!selectedFiles.length) {
      return { ok: false, message: 'Please select a file before uploading.' };
    }

    const names = selectedFiles.map((f: any) => String(f?.name ?? '').toLowerCase());
    const extOf = (name: string) => {
      const i = name.lastIndexOf('.');
      return i < 0 ? '' : name.slice(i);
    };
    const baseOf = (name: string) => {
      const i = name.lastIndexOf('.');
      return i < 0 ? name : name.slice(0, i);
    };

    if (sourceFormat === FileTypes.SHAPEFILE) {
      const required = ['.shp', '.shx', '.dbf', '.prj'];
      const have: Record<string, string[]> = {};
      for (const n of names) {
        const ext = extOf(n);
        if (!have[ext]) have[ext] = [];
        have[ext].push(baseOf(n));
      }
      const missing = required.some((ext) => !have[ext] || have[ext].length === 0);
      const bases = required.map((ext) => have[ext]?.[0]);
      const baseMismatch = bases.some((b) => b !== bases[0]);
      if (missing || baseMismatch) {
        return {
          ok: false,
          message: 'Invalid shapefile upload. Ensure all required files (.shp, .shx, .dbf, .prj) are included with the same name',
        };
      }
      return { ok: true };
    }

    const allowed = (allowedExtensions ?? []).map((e) => e.toLowerCase());
    const anyMatches = names.some((n) => allowed.some((ext) => n.endsWith(ext)));
    if (!anyMatches) {
      return { ok: false, message: 'File type must match the existing dataset type' };
    }
    return { ok: true };
  };

  const handleUploadClick = () => {
    const check = validateReuploadFiles();
    if (!check.ok) {
      setValidationError(check.message);
      return;
    }
    setValidationError('');
    presignedFileUpload(
      folderId,
      (value) => setS3UniqueFolderName(value),
      true,
      () => {
        setIsReuploadVisible(false);
      },
    );
  };

  // US-101368 Scenario 8: confirm before discarding a pending re-upload so
  // an accidental click on the X doesn't trash the user's selection.
  const handleReuploadCancel = () => {
    const closeNow = () => {
      setIsReuploadVisible(false);
      setIsReUpload?.(false);
      setSelectedFiles([]);
    };
    if (selectedFiles.length === 0) {
      closeNow();
      return;
    }
    Modal.confirm({
      title: 'Are you sure you want to cancel the upload?',
      okText: 'Cancel upload',
      cancelText: 'Keep selection',
      onOk: closeNow,
    });
  };

  const handleRemoveFile = useCallback(
    (file: UploadFile<any>) => {
      setSelectedFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    [setSelectedFiles],
  );

  return (
    <>
      <div className="lp-section">
        <div className="lp-section__header">LAYER DESCRIPTION</div>
        <div className="lp-section__field">
          <Input
            placeholder="Enter layer description"
            value={layerDescription}
            onChange={(e) => setState((prev) => ({ ...prev, layerDescription: e.target.value }))}
          />
        </div>
      </div>
      <div className="lp-section" aria-labelledby="layer-data-source-label">
        <div className="lp-section__header" id="layer-data-source-label">
          DATA SOURCE
        </div>
        {isMappingPending ? (
          <Spin size="small" />
        ) : sourcePath.length ? (
          <a
            className="lp-data-source__link"
            href={`/files/all-files?folderId=${encodeURIComponent(folderId)}`}
            target="_blank"
            rel="noreferrer"
            aria-label="Open this data source in File Manager"
          >
            <Breadcrumb
              items={sourcePath.map((item) => ({
                title: item.name,
              }))}
            />
          </a>
        ) : (
          <span className="lp-data-source__empty">No source folder available</span>
        )}
      </div>
      <div className="lp-section">
        <div className="lp-section__header">SCALE RANGE</div>
        <div className="lp-section__field">
          <Select
            placeholder="Select Minimum"
            value={scaleRange?.min || undefined}
            onChange={(value) =>
              setState((prev) => ({
                ...prev,
                scaleRange: { ...scaleRange, min: value },
              }))
            }
          >
            {mapScales.map((scale) => (
              <Option key={scale.value} value={scale.value}>
                {scale.label}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Select Maximum"
            value={scaleRange?.max || undefined}
            onChange={(value) =>
              setState((prev) => ({
                ...prev,
                scaleRange: { ...scaleRange, max: value },
              }))
            }
          >
            {mapScales.map((scale) => (
              <Option key={scale.value} value={scale.value}>
                {scale.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="lp-section">
        <div className="lp-section__header">DISPLAY OPTIONS</div>
        <div>
          <div className="lp-section__toggle-row">
            <HintLabel label="Display on Map" hint={DISPLAY_ON_MAP_HINT} className="lp-section__toggle-label" />
            <Switch checked={displayOnMap} onChange={(checked) => setState((prev) => ({ ...prev, displayOnMap: checked }))} />
          </div>
          <div className="lp-section__toggle-row">
            <HintLabel label="Display in Legend" hint={DISPLAY_IN_LEGEND_HINT} className="lp-section__toggle-label" />
            <Switch checked={displayInLegend} onChange={(checked) => setState((prev) => ({ ...prev, displayInLegend: checked }))} />
          </div>
          <div className="lp-section__toggle-row">
            <HintLabel label="Downloadable" hint={DOWNLOADABLE_HINT} className="lp-section__toggle-label" />
            <Switch checked={downloadable} onChange={(checked) => setState((prev) => ({ ...prev, downloadable: checked }))} />
          </div>
        </div>
      </div>
      {isExistingLayer && (
        <ConditionalRender visible={canReupload}>
          <div className="lp-section">
            <div className="lp-section__header">EDIT DATA</div>
            <div className="lp-section__field">
              <Button onClick={handleReUploadData}>Re-Upload Data</Button>
              <Button>Update Permissions</Button>
            </div>
          </div>
        </ConditionalRender>
      )}

      <Modal
        title={
          <div>
            <div>REUPLOAD FILE</div>
            {sourceFormat && layerTitle && <div className="lp-modal__reupload-subtitle">{`${sourceFormat.toUpperCase()}: ${layerTitle}`}</div>}
          </div>
        }
        open={isReuploadVisible}
        onCancel={handleReuploadCancel}
        footer={null}
        centered
        width={600}
        className="lp-reupload-modal"
      >
        <FileUpload
          folderId={folderId}
          selectedFiles={selectedFiles}
          handleFileChange={handleFileChange}
          allowedExtensions={allowedExtensions}
          validationError={validationError}
          onRemove={handleRemoveFile}
        />
        <div className="modal-footer">
          <Button className="lp-modal__upload-btn" onClick={handleUploadClick} disabled={isUploading}>
            UPLOAD
          </Button>
        </div>
      </Modal>
      <UploadProgressModal uploadProgress={uploadProgress} onCancel={cancelUpload} />
    </>
  );
};

export default LayerSettingsTab;
